import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyContent,
  ListItem,
  ScrollArea,
  Text,
  TextField,
} from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useChangeStudy } from '@/services/authHooks';
import { Study } from '@/types/auth';
import { IconBuilding, IconChevronDown, IconSearch } from '@tabler/icons-react';

const StudySelector: React.FC = () => {
  const { user } = useAuth();
  const { mutate: changeStudy, isPending } = useChangeStudy();
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const currentStudy = user?.current_study;
  const permittedStudies = useMemo(
    () => user?.permitted_studies || [],
    [user?.permitted_studies]
  );

  // Filter studies based on search query
  const filteredStudies = useMemo(() => {
    if (!searchQuery.trim()) return permittedStudies;

    const query = searchQuery.toLowerCase();
    return permittedStudies.filter(
      study =>
        study.name.toLowerCase().includes(query) ||
        (study.description && study.description.toLowerCase().includes(query))
    );
  }, [permittedStudies, searchQuery]);

  const handleStudyChange = (study: Study) => {
    if (study.id === currentStudy?.id) return;

    changeStudy(study.id);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Always show if user has a current study, even if only one study
  if (!currentStudy) {
    return null;
  }

  const triggerLabel = (
    <ListItem
      primary={currentStudy.name}
      // secondary={currentStudy.description || 'Current Study'}
      endIcon={<IconChevronDown />}
      startIcon={<IconBuilding />}
    />
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger>
        <Button asChild variant="soft" size="small" disabled={isPending}>
          {triggerLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" variant="soft" style={{ width: 320 }}>
        {/* Search */}
        {permittedStudies.length > 1 && (
          <Box p="2">
            <TextField
              startSlot={<IconSearch />}
              placeholder="Search studies..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              size="small"
            />
          </Box>
        )}

        {/* Studies List */}
        <ScrollArea scrollbars="vertical">
          <Box p="2" pb="4">
            {filteredStudies.length === 0 ? (
              <>
                {searchQuery ? (
                  <EmptyContent
                    size="small"
                    icon={<IconBuilding />}
                    title={'No studies found'}
                    description="Try adjusting your search query"
                  />
                ) : (
                  <EmptyContent
                    size="small"
                    icon={<IconBuilding />}
                    title={'No studies available'}
                    description="Contact your administrator for access"
                  />
                )}
              </>
            ) : (
              filteredStudies.map(study => (
                <ListItem
                  key={study.id}
                  primary={study.name}
                  secondary={study.description}
                  startIcon={<IconBuilding />}
                  onClick={() => handleStudyChange(study)}
                  selected={study.id === currentStudy?.id}
                  disabled={isPending}
                  className="hover-effect study-switcher-item"
                />
              ))
            )}
          </Box>
        </ScrollArea>

        {permittedStudies.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <Box p="1">
              <Text
                size="2xsmall"
                color="gray"
                style={{ textAlign: 'center', display: 'block' }}
              >
                {`${permittedStudies.length} ${permittedStudies.length === 1 ? 'study' : 'studies'} available`}
              </Text>
            </Box>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default StudySelector;
